import { ColDef, GridOptions, ITooltipParams } from '@ag-grid-community/core';
import dayjs from 'dayjs';

import { AgRawTooltipValue } from '@shared/components/ag-raw-tooltip/ag-raw-tooltip';
import { EDITABLE_HEADER } from '@shared/constants/ag-classes';
import { OverlayService } from '@shared/services/overlay.service';

import { createdColumnGroup } from './created-column-group';
import { DEFAULT_ADJUSTMENTS_TO_PREPAID_GRID_CONFIG } from './default-grid-config';
import { GridConfigOptions } from './grid-config-context';
import { invoiceDetailsColumnGroup } from './invoice-details-column-group';
import { prepaidImpactColumnGroup } from './prepaid-impact-column-group';
import { AdjustmentsToPrepaidRow } from '../adjustments-to-prepaid.model';

export function adjustmentsToPrepaidGridConfig(
  options: GridConfigOptions,
  overlayService: OverlayService
): GridOptions<AdjustmentsToPrepaidRow> {
  const defaultGridConfig = DEFAULT_ADJUSTMENTS_TO_PREPAID_GRID_CONFIG;
  const { currentOpenMonth, editMode, isCellEditable, status } = options;

  return <GridOptions<AdjustmentsToPrepaidRow>>{
    ...defaultGridConfig,
    columnDefs: [
      getCheckboxColumn(options),
      ...prepaidImpactColumnGroup(options, overlayService),
      ...invoiceDetailsColumnGroup(options),
      ...createdColumnGroup(options),
    ],
    defaultColDef: {
      ...defaultGridConfig.defaultColDef,
      editable: (params) => editMode() && !params.node.rowPinned && isCellEditable(params),
      headerClass: (params) => {
        const classes = ['text-center'];
        const nonEditableColumns = [
          'actions',
          'total_impact_to_prepaid',
          'total_expense_per_invoice',
          'created_by',
          'create_date',
        ];

        if (editMode() && !nonEditableColumns.includes(params.column?.getColDef().field || '')) {
          classes.push(EDITABLE_HEADER);
        }

        return classes;
      },
    },
    isRowSelectable: (node) => {
      // Disable selection for rows with Posting Period = Closed Month for Trials with status = Live/Archived
      const data = node.data;

      if (!editMode()) return false;

      if (!data) return false;

      if (
        (status === 'IMPLEMENTATION_STATUS_LIVE' || status === 'IMPLEMENTATION_STATUS_ARCHIVED') &&
        data.accrual_period &&
        currentOpenMonth &&
        dayjs(data.accrual_period).isBefore(dayjs(currentOpenMonth))
      ) {
        return false;
      }

      return true;
    },
  };
}

function getCheckboxColumn(options: GridConfigOptions): ColDef<AdjustmentsToPrepaidRow> {
  const { editMode } = options;

  return {
    cellClass: '!flex items-center justify-center !p-0 ag-selection-checkbox-cell',
    checkboxSelection: true,
    colId: 'selection',
    editable: false,
    getQuickFilterText: () => '',
    headerCheckboxSelection: true,
    headerName: '',
    hide: !editMode(),
    lockPosition: true,
    maxWidth: 40,
    minWidth: 40,
    suppressMenu: true,
    suppressMovable: true,
    tooltipValueGetter: (params: ITooltipParams) => {
      const isRowSelectable = params.node?.selectable ?? true;

      if (!isRowSelectable) {
        return <AgRawTooltipValue>{
          forceTooltip: true,
          value: 'Cannot edit Prepaid Adjustments in Closed Months',
        };
      }

      return '';
    },
    valueFormatter: () => '',
    width: 40,
  };
}
